import { Component, FunctionComponent } from "react";
import { RouteComponentProps } from "react-router-dom";
import { ConfirmShopCreationInput, ConfirmShopCreationOutput } from "../../client/core";
import { Card, Col, Container, OverlayTrigger, Popover, Spinner, Button as BButton } from "react-bootstrap";
import { Color, Path, Storage } from "../../env";
import { Button, variant } from "../form/Button";
import WebsitePreview from "./WebsitePreview";

export interface ShopReferralConfirmationState {
    shopName: string
    shopEmail: string
    shopId: string
    status: string
    isLoading: boolean
    error: boolean
}

export interface ShopReferralConfirmationProps extends RouteComponentProps {
    confirmShopCreation: (input: ConfirmShopCreationInput) => Promise<ConfirmShopCreationOutput>
}

export default class ShopReferralConfirmation extends Component<ShopReferralConfirmationProps, ShopReferralConfirmationState> {
    constructor(props: ShopReferralConfirmationProps) {
        super(props);
        this.state = {
            shopEmail: "",
            shopName: "",
            shopId: "",
            status: "",
            isLoading: true,
            error: false,
        };
    }

    componentDidMount(): void {
        const queryParams = new URLSearchParams(this.props.history.location.search)

        const shopName = queryParams.get("shopname") || ""
        const status = queryParams.get("status") || "REJECTED"
        const shopEmail = queryParams.get("shopemail") || ""
        const shopId = queryParams.get("shopid") || ""

        localStorage.setItem(Storage.OpenShop, shopId)

        this.props.confirmShopCreation({
            email: shopEmail,
            status: status,
        }).then(session => {
            if (session.statusCode !== 200) {
                return this.setState({
                    isLoading: false,
                    error: true,
                })
            }

            this.setState({
                isLoading: false,
                shopName: shopName,
                status: status,
                shopEmail: shopEmail,
                shopId: shopId
            })
        })
    }

    handleContinue(): void {
        this.props.history.push(`${Path.RegisterMember}?email=${this.state.shopEmail}&redirect=${Path.RegisterShop}`)
    }

    render() {
        if (this.state.isLoading) {
            return <div style={{ textAlign: "center" }}><Spinner variant="success" animation="border" /></div>
        }

        const props: ShopReferralConfirmationViewProps = {
            shopEmail: this.state.shopEmail,
            shopName: this.state.shopName,
            shopId: this.state.shopId,
            status: this.state.status,
            onContinue: () => this.handleContinue(),
        };

        if (this.state.error) {
            return <ErrorView {...props} />
        }

        if (this.state.status == "REJECTED") {
            return <ShopRejectedConfirmationView {...props} />
        }

        return <ShopAcceptedConfirmationView {...props} />;
    }
}

export interface ShopReferralConfirmationViewProps {
    shopName: string
    shopEmail: string
    shopId: string
    status: string
    onContinue: () => void
}

export const ShopAcceptedConfirmationView: FunctionComponent<ShopReferralConfirmationViewProps> = (props) => (
    <div>
        <h2>🎉 Welcome to Stumbled!</h2>
        <p>{props.shopName} is live and ready for customers to stumble upon it and see what makes you unique.</p>
        <p>Other local shops are adding their site to Instagram and Facebook — and customers are noticing! Why not join in?</p>
        <Button style={{ marginBottom: "-0.3rem" }} borderRadius={20} name="Visit your shop" onClick={() => {
            window.open(`https://${process.env.REACT_APP_BASE_DOMAIN}/${props.shopId}`, "_blank");
        }} buttonvariant={variant.Primary} />
        <OverlayTrigger trigger="click" placement="left" rootClose overlay={(
            <Popover id="popover-basic">
                <Popover.Body>
                    Site link copied
                </Popover.Body>
            </Popover>
        )}>
            <BButton style={{ float: "right", backgroundColor: "#E1306C", border: 0, marginBottom: "-0.3rem" }} variant={"success"} onClick={() => navigator.clipboard.writeText(`https://${process.env.REACT_APP_BASE_DOMAIN}/${props.shopId}`)} >Copy link</BButton>
        </OverlayTrigger>
        <div style={{ border: `.3rem solid ${Color.Primary}`, borderRadius: "5px", marginBottom: "2rem" }}><WebsitePreview url={`https://${process.env.REACT_APP_BASE_DOMAIN}/${props.shopId}`} /></div>
        <p>Did you know, because you've been personally recommended by our founder, you can create a shop for <span style={{ color: Color.Primary }}>free forever</span>. Adding products is as easy as posting online, with payments and till systems integrated automatically! ✅</p>
        <Button style={{ float: "right" }} onClick={() => props.onContinue()} buttonvariant={variant.Primary} name="Continue" />
    </div>
);

export const ShopRejectedConfirmationView: FunctionComponent<ShopReferralConfirmationViewProps> = (props) => (
    <div>
        <h2>We understand!</h2>
        <p>Thanks for considering us, {props.shopName}. We completely respect your decision and appreciate your time. If you ever change your mind, we’re here to help connect more people with amazing local shops like yours.</p>
        <p>If you’d like to chat or learn more about what we’re doing for local businesses, feel free to reach out anytime. Here’s to supporting our local community, whether it’s now or down the road.</p>
    </div>
);

export interface ErrorViewProps {
    onContinue: () => void
}

export const ErrorView: FunctionComponent<ErrorViewProps> = (props) => (
    <Container style={{ padding: "1rem" }} fluid>
        <Col xs={12} md={10} style={{ margin: "auto" }}>
            <Card className={"elevate"} style={{ padding: "1rem" }}>
                <Card.Body>
                    <div>
                        <p>We'll register your details shortly.</p>
                        <p>Sorry, we were unable set your preference at this time. Don't worry, we've got it sorted and will confirm your details from our side - you don't need to do anything.</p>
                        <p>If you’d like to set-up an online shop, simply register, add a few products, and you’re all set.</p>
                        <Button style={{ float: "right" }} onClick={() => props.onContinue()} buttonvariant={variant.Primary} name="Continue" />
                    </div>
                </Card.Body>
            </Card>
        </Col>
    </Container>
);
